import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { CustomerShell } from "@/components/CustomerShell";
import { rupee } from "@/lib/constants";
import { toast } from "sonner";
import {
    ChevronLeft, Loader2, MapPin, Plus, X, Trash2, Wind, Flame, BatteryCharging, Droplets,
    Refrigerator, WashingMachine, Wrench, ShieldCheck, Star, History, Users, CalendarPlus,
    RotateCcw, Pencil, Gauge, Package, CheckCircle2,
} from "lucide-react";

const ITEM_TYPES = [
    { key: "AC", icon: Wind }, { key: "Geyser", icon: Flame }, { key: "Inverter", icon: BatteryCharging },
    { key: "RO / Water Purifier", icon: Droplets }, { key: "Refrigerator", icon: Refrigerator },
    { key: "Washing Machine", icon: WashingMachine }, { key: "Chimney", icon: Wrench }, { key: "Other", icon: Wrench },
];
const iconFor = (type) => (ITEM_TYPES.find((i) => i.key === type)?.icon) || Wrench;
const scoreColor = (s) => s >= 80 ? "text-emerald-600" : s >= 60 ? "text-amber-600" : "text-red-500";

export default function HomePassport() {
    const { homeId } = useParams();
    const nav = useNavigate();
    const [h, setH] = useState(null);
    const [addItem, setAddItem] = useState(false);
    const [addPast, setAddPast] = useState(false);
    const [renaming, setRenaming] = useState(false);

    const load = useCallback(() => api.get(`/homes/${homeId}`).then((r) => setH(r.data)).catch(() => toast.error("Could not load home")), [homeId]);
    useEffect(() => { load(); }, [load]);

    if (!h) return <CustomerShell noNav><div className="flex min-h-screen items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div></CustomerShell>;

    const c = h.counters || {};
    const removeItem = async (itemId) => {
        try { await api.delete(`/homes/${homeId}/items/${itemId}`); toast.success("Removed"); load(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    const bookHere = (serviceId) => nav(`/book?home=${homeId}${serviceId ? `&service=${serviceId}` : ""}`);

    // group history by year
    const byYear = {};
    (h.history || []).forEach((it) => { const y = (it.date || "----").slice(0, 4); (byYear[y] ||= []).push(it); });
    const years = Object.keys(byYear).sort().reverse();

    return (
        <CustomerShell noNav>
            {/* header */}
            <div className="rounded-b-3xl bg-navy px-5 pb-6 pt-5 text-white">
                <div className="flex items-center gap-3">
                    <button data-testid="passport-back-btn" onClick={() => nav("/my-home")} className="rounded-full bg-white/10 p-2 hover:bg-white/20"><ChevronLeft size={20} /></button>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <h1 className="truncate font-heading text-xl font-extrabold">{h.label}</h1>
                            <button data-testid="rename-home-btn" onClick={() => setRenaming(true)} className="rounded-full bg-white/10 p-1"><Pencil size={13} /></button>
                        </div>
                        <p className="text-xs text-white/55">{h.home_id}</p>
                    </div>
                </div>
                <div className="mt-2 flex items-start gap-1.5 text-sm text-white/75"><MapPin size={15} className="mt-0.5 shrink-0 text-orange" />{h.address_line}{h.society ? `, ${h.society}` : ""}{h.area_name ? ` · ${h.area_name}` : ""}</div>

                <div className="mt-4 flex items-stretch gap-3">
                    <div className="flex flex-1 items-center gap-3 rounded-2xl bg-white/10 p-3">
                        <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-white">
                            <span className={`font-heading text-lg font-extrabold ${scoreColor(h.home_score)}`}>{h.home_score}</span>
                        </div>
                        <div><p className="text-[11px] uppercase tracking-wide text-white/50">Home Health</p><p className="font-heading text-sm font-bold">Score {h.home_score}/100</p></div>
                    </div>
                    <div className="flex flex-1 flex-col justify-center rounded-2xl bg-white/10 p-3">
                        <p className="text-[11px] uppercase tracking-wide text-white/50">Last service</p>
                        <p className="font-heading text-sm font-bold">{c.last_service || "—"}</p>
                    </div>
                </div>

                <button data-testid="book-for-home-btn" onClick={() => bookHere()} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-orange py-3.5 text-sm font-extrabold text-white shadow-lg shadow-orange/30 active:scale-95"><Plus size={18} /> BOOK SERVICE FOR THIS HOME</button>
            </div>

            <div className="space-y-5 px-5 py-5">
                {/* counters */}
                <div className="grid grid-cols-3 gap-2.5">
                    <Stat icon={History} label="Total" value={c.total ?? 0} />
                    <Stat icon={CalendarPlus} label="This year" value={c.this_year ?? 0} />
                    <Stat icon={Users} label="Pref. Pros" value={c.preferred_pros ?? 0} />
                    <Stat icon={ShieldCheck} label="CareBack" value={c.careback_active ?? 0} />
                    <Stat icon={Package} label="Appliances" value={(h.appliances || []).length} />
                    <Stat icon={Gauge} label="Score" value={h.home_score} />
                </div>

                {/* category breakdown */}
                {c.by_category?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {c.by_category.map((b) => <span key={b.category} className="rounded-full bg-navy/5 px-2.5 py-1 text-xs font-semibold capitalize text-navy/70">{(b.category || "other").replace("_", " ")}: {b.count}</span>)}
                    </div>
                )}

                {/* preferred pros */}
                {h.preferred_pros?.length > 0 && (
                    <div>
                        <h2 className="mb-2 font-heading text-base font-bold text-navy">My HOMIIE Team</h2>
                        <div className="space-y-2">
                            {h.preferred_pros.map((p) => (
                                <div key={p.id} className="flex items-center gap-3 rounded-2xl border border-navy/10 bg-white p-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-navy text-base font-bold text-orange">{p.name[0]}</div>
                                    <div className="flex-1"><p className="font-heading text-sm font-bold text-navy">{p.name}</p><p className="text-xs capitalize text-navy/50">{(p.services || []).join(", ")} · ⭐ {p.rating || "New"}</p></div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* appliances */}
                <div>
                    <div className="mb-2 flex items-center justify-between">
                        <h2 className="font-heading text-base font-bold text-navy">Appliances</h2>
                        <button data-testid="add-item-btn" onClick={() => setAddItem(true)} className="flex items-center gap-1 rounded-full bg-navy px-2.5 py-1 text-xs font-bold text-white"><Plus size={13} /> Add</button>
                    </div>
                    {(h.appliances || []).length === 0 && <p className="text-xs text-navy/40">Add your AC, geyser, RO & appliances to track warranty and repairs.</p>}
                    <div className="space-y-2">
                        {(h.appliances || []).map((it) => {
                            const Icon = iconFor(it.type);
                            return (
                                <div key={it.id} className="flex items-center gap-2.5 rounded-xl border border-navy/10 bg-white p-2.5" data-testid={`passport-item-${it.id}`}>
                                    <div className="rounded-lg bg-orange/10 p-1.5"><Icon size={16} className="text-orange" /></div>
                                    <div className="flex-1"><p className="text-sm font-semibold text-navy">{it.type}{it.brand ? ` · ${it.brand}` : ""}</p><p className="text-xs text-navy/45">{it.warranty_date ? `Warranty till ${it.warranty_date}` : ""}{it.notes ? ` · ${it.notes}` : ""}</p></div>
                                    <button data-testid={`delete-item-${it.id}`} onClick={() => removeItem(it.id)} className="p-1 text-navy/30 hover:text-red-500"><Trash2 size={15} /></button>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* service history timeline */}
                <div>
                    <div className="mb-3 flex items-center justify-between">
                        <h2 className="font-heading text-base font-bold text-navy">Service history</h2>
                        <button data-testid="add-past-btn" onClick={() => setAddPast(true)} className="flex items-center gap-1 rounded-full border border-navy/15 px-2.5 py-1 text-xs font-bold text-navy"><Plus size={13} /> Past service</button>
                    </div>
                    {years.length === 0 && <p className="text-xs text-navy/40">No services yet. Book your first service for this home.</p>}
                    {years.map((y) => (
                        <div key={y} className="mb-4">
                            <p className="mb-2 font-heading text-sm font-extrabold text-orange">{y}</p>
                            <div className="space-y-2.5">
                                {byYear[y].map((it, i) => (
                                    <div key={i} className="rounded-2xl border border-navy/10 bg-white p-3.5" data-testid={`history-${y}-${i}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-heading text-sm font-bold text-navy">{it.service_name.replace(" Visit", "")}</h3>
                                                    {it.source === "External" ? <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">Customer added</span> : <span className="flex items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700"><CheckCircle2 size={9} /> HOMIIE</span>}
                                                </div>
                                                <p className="mt-0.5 text-xs text-navy/55">{it.date}{it.professional_name ? ` · ${it.professional_name}` : ""}</p>
                                            </div>
                                            <div className="text-right">
                                                {it.amount != null && <p className="font-heading text-sm font-extrabold text-navy">{rupee(it.amount)}</p>}
                                                {it.rating && <p className="flex items-center justify-end gap-0.5 text-xs text-navy/50"><Star size={11} className="fill-orange text-orange" />{it.rating}</p>}
                                            </div>
                                        </div>
                                        <div className="mt-2 flex gap-2">
                                            {it.booking_id && <button onClick={() => nav(`/bookings/${it.booking_id}`)} className="flex-1 rounded-full border border-navy/15 py-1.5 text-xs font-bold text-navy">View details</button>}
                                            {it.service_id && <button data-testid={`book-again-${y}-${i}`} onClick={() => bookHere(it.service_id)} className="flex flex-1 items-center justify-center gap-1 rounded-full bg-orange/10 py-1.5 text-xs font-bold text-orange-dark"><RotateCcw size={12} /> Book again</button>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {addItem && <AddItem homeId={homeId} onClose={() => setAddItem(false)} onDone={() => { setAddItem(false); load(); }} />}
            {addPast && <AddPast homeId={homeId} onClose={() => setAddPast(false)} onDone={() => { setAddPast(false); load(); }} />}
            {renaming && <Rename homeId={homeId} current={h.label} onClose={() => setRenaming(false)} onDone={() => { setRenaming(false); load(); }} />}
        </CustomerShell>
    );
}

function Stat({ icon: Icon, label, value }) {
    return (
        <div className="rounded-2xl border border-navy/10 bg-white p-3 text-center">
            <Icon size={16} className="mx-auto text-orange" />
            <p className="mt-1 font-heading text-lg font-extrabold text-navy">{value}</p>
            <p className="text-[11px] text-navy/50">{label}</p>
        </div>
    );
}

function Shell({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-navy/50 backdrop-blur-sm" onClick={onClose}>
            <div className="mx-auto max-h-[85vh] w-full max-w-md animate-fade-up overflow-y-auto rounded-t-3xl bg-white p-6 pb-8 homiie-scroll" onClick={(e) => e.stopPropagation()}>
                <div className="mb-3 flex items-center justify-between"><h3 className="font-heading text-lg font-extrabold text-navy">{title}</h3><button onClick={onClose}><X size={20} className="text-navy/50" /></button></div>
                {children}
            </div>
        </div>
    );
}

function AddItem({ homeId, onClose, onDone }) {
    const [f, setF] = useState({ type: "AC", brand: "", warranty_date: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try { await api.post(`/homes/${homeId}/items`, f); toast.success("Added to Home Passport"); onDone(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
    };
    return (
        <Shell title="Add appliance" onClose={onClose}>
            <div className="mb-3 grid grid-cols-4 gap-2">
                {ITEM_TYPES.map((it) => { const Icon = it.icon; return (
                    <button key={it.key} data-testid={`type-${it.key.replace(/[^a-z]/gi, "").toLowerCase()}`} onClick={() => setF({ ...f, type: it.key })} className={`flex flex-col items-center gap-1 rounded-xl border p-2 text-[10px] font-semibold ${f.type === it.key ? "border-orange bg-orange/5 text-navy" : "border-navy/10 text-navy/60"}`}><Icon size={18} className={f.type === it.key ? "text-orange" : "text-navy/50"} />{it.key.split(" ")[0]}</button>
                ); })}
            </div>
            <input data-testid="item-brand" value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} placeholder="Brand (e.g. Voltas)" className="mb-2 w-full rounded-xl border border-navy/15 px-4 py-3 text-sm outline-none focus:border-orange" />
            <input data-testid="item-warranty" value={f.warranty_date} onChange={(e) => setF({ ...f, warranty_date: e.target.value })} placeholder="Warranty valid till (e.g. 2027-05)" className="mb-2 w-full rounded-xl border border-navy/15 px-4 py-3 text-sm outline-none focus:border-orange" />
            <input data-testid="item-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Notes (optional)" className="mb-3 w-full rounded-xl border border-navy/15 px-4 py-3 text-sm outline-none focus:border-orange" />
            <button data-testid="save-item-btn" disabled={busy} onClick={submit} className="w-full rounded-full bg-orange py-3 text-sm font-bold text-white disabled:opacity-60">SAVE</button>
        </Shell>
    );
}

function AddPast({ homeId, onClose, onDone }) {
    const [f, setF] = useState({ service_name: "", date: "", provider: "Outside HOMIIE", amount: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!f.service_name.trim() || !f.date.trim()) return toast.error("Service name & date required");
        setBusy(true);
        try { await api.post(`/homes/${homeId}/history`, { ...f, amount: f.amount ? +f.amount : null }); toast.success("Past service added"); onDone(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
    };
    return (
        <Shell title="Add past / external service" onClose={onClose}>
            <p className="mb-3 text-xs text-navy/55">Services done before or outside HOMIIE. These are clearly marked "Customer added".</p>
            <input data-testid="past-service" value={f.service_name} onChange={(e) => setF({ ...f, service_name: e.target.value })} placeholder="Service (e.g. AC Installation)" className="mb-2 w-full rounded-xl border border-navy/15 px-4 py-3 text-sm outline-none focus:border-orange" />
            <input data-testid="past-date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} placeholder="Date (YYYY-MM-DD)" className="mb-2 w-full rounded-xl border border-navy/15 px-4 py-3 text-sm outline-none focus:border-orange" />
            <input data-testid="past-provider" value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} placeholder="Provider" className="mb-2 w-full rounded-xl border border-navy/15 px-4 py-3 text-sm outline-none focus:border-orange" />
            <input data-testid="past-amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Amount (optional)" className="mb-2 w-full rounded-xl border border-navy/15 px-4 py-3 text-sm outline-none focus:border-orange" />
            <input data-testid="past-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Notes (optional)" className="mb-3 w-full rounded-xl border border-navy/15 px-4 py-3 text-sm outline-none focus:border-orange" />
            <button data-testid="save-past-btn" disabled={busy} onClick={submit} className="w-full rounded-full bg-orange py-3 text-sm font-bold text-white disabled:opacity-60">ADD TO HISTORY</button>
        </Shell>
    );
}

function Rename({ homeId, current, onClose, onDone }) {
    const [label, setLabel] = useState(current);
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!label.trim()) return toast.error("Enter a name");
        setBusy(true);
        try { await api.patch(`/homes/${homeId}`, { label }); toast.success("Home renamed"); onDone(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
    };
    return (
        <Shell title="Home name" onClose={onClose}>
            <input data-testid="rename-input" value={label} onChange={(e) => setLabel(e.target.value)} className="mb-3 w-full rounded-xl border border-navy/15 px-4 py-3 text-sm outline-none focus:border-orange" />
            <button data-testid="save-rename-btn" disabled={busy} onClick={submit} className="w-full rounded-full bg-orange py-3 text-sm font-bold text-white disabled:opacity-60">SAVE</button>
        </Shell>
    );
}
